import { PropsWithChildren } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../../store/types';
import {
  DEMO_ACCOUNT_OTP_CODE,
  DEMO_ACCOUNT_PHONE_NUMBER,
  OTP_DELIVERY_CHANNELS,
  REDUX_STORE_SLICES,
} from '../../../../services/exports/Constants';
import { OtpSchema, otpSchema } from './schema';
import { zodResolver } from '@hookform/resolvers/zod';
import useAuth from '../../../../hooks/global/useAuth';
import useAnalytics from '../../../../hooks/analytics/useAnalytics';
import Button from '../../components/buttons/Button';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import useMobileApp from '../../../../hooks/mobile-app/useMobileApp';
import { LoginData } from '../../../../services/auth/AuthorizationManager';
import { resolveOtpFailure } from '../../../../utils/otp';
import Captcha from '../../../../components-library/auth/turnstile/Captcha';
import { ITurnstileVerifyPayload } from '../../../../components-library/auth/turnstile/types';
import OtpVerificationStep from '../../../../components-library/auth/OtpVerificationStep';
import useOtpChannels from '../../../../hooks/auth/useOtpChannels';

interface OtpFormProps extends PropsWithChildren {
  onSuccess: () => void;
  defaultValues: OtpSchema;
  onResendClick: (channel?: OTP_DELIVERY_CHANNELS) => void;
  sentChannel?: OTP_DELIVERY_CHANNELS;
  // Masked address the code was sent to, straight from the OTP response.
  deliveryAddress?: string;
  resending?: boolean;
  // Resend lockout countdown (seconds). The captcha reappears and resend unlocks at 0.
  retryAfter?: number;
  hasTurnstile?: boolean;
  attachToken?: (payload: ITurnstileVerifyPayload) => void;
}

const OtpForm = ({
  onSuccess,
  defaultValues,
  onResendClick,
  sentChannel,
  deliveryAddress,
  resending,
  retryAfter = 0,
  hasTurnstile,
  attachToken,
  children,
}: OtpFormProps) => {
  const { login, loading } = useAuth();
  const companyId = useSelector((state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA].company?.id);
  const form = useForm<OtpSchema>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      phone_number: defaultValues?.phone_number,
      company_id: companyId,
      password:
        defaultValues?.password ??
        (defaultValues?.phone_number === DEMO_ACCOUNT_PHONE_NUMBER ? DEMO_ACCOUNT_OTP_CODE : ''),
    },
  });
  const { isMobileApp } = useMobileApp();
  // Fallback for the brief window before the first send resolves a channel: the
  // provider's own default, so an email-only partner never reads "Sent via WhatsApp".
  const { defaultChannel } = useOtpChannels();
  const { trackOtpVerified, trackOtpVerificationFailed } = useAnalytics();
  const onSubmitForm = async (data: OtpSchema) => {
    const result = await login({ ...data, ...(isMobileApp && { utm_source: 'mobile_app' }) } as LoginData);
    const otp_channel = sentChannel ?? defaultChannel;

    if (result?.success) {
      trackOtpVerified({ otp_channel });

      return onSuccess();
    }

    // Keep the modal open on failure and tell the user why — otherwise a wrong code
    // would silently close the modal with no feedback.
    const failure = resolveOtpFailure(result);
    trackOtpVerificationFailed({ otp_channel, ...failure });
    toast.error(t(`feedback.${failure.failure_reason}`));
  };

  const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Modals:Login:OtpForm' });

  return (
    <form onSubmit={form.handleSubmit(onSubmitForm)} className={!children ? 'pb-sm' : ''}>
      <Controller
        name="password"
        control={form.control}
        render={({ field }) => (
          <OtpVerificationStep
            code={field.value ?? ''}
            onCodeChange={field.onChange}
            phoneNumber={defaultValues?.phone_number}
            deliveryAddress={deliveryAddress}
            channel={sentChannel ?? defaultChannel}
            onResend={onResendClick}
            retryAfter={retryAfter}
            resending={resending}
            turnstile={hasTurnstile && <Captcha className="text-center" onVerify={attachToken} />}
            shouldAutoFocus
          >
            <Button title={t('buttons.login')} type="submit" loading={loading} expand="block" />
          </OtpVerificationStep>
        )}
      />
      {children}
    </form>
  );
};

export default OtpForm;
